import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import moment from "moment";
import classNames from "classnames";
import {
  Column,
  ResponsiveRow,
  Row,
  Spacer,
  TwoColGrid,
} from "../../../components/Layout/Layout";
import { Role, RoleFeature } from "../../../components/RoleFeature";
import { ObjectDetailsModel } from "../../../core/api/objects/types";
import { AlarmHandling } from "../../../core/api/alarmhandlings";
import { IItemProp } from "../../../ui-lib/components/Dropdown/DropdownItem";
import {
  breakpoints,
  useWindowDimensions,
} from "../../../core/hooks/dimensionProvider";
import {
  ITreeData,
  CustomDropdown,
} from "../../../ui-lib/components/Hierarchy/CustomTreeDropdown/CustomDropdown";
import useTranslations from "../../../core/i18n/useTranslations";
import TextInput from "../../../ui-lib/components/Inputs/TextInput";
import useUser from "../../../core/user/useUser";
import PhotoWrapperWithUpload from "../../../ui-lib/components/PhotoWrapperWithUpload/PhotoWrapperWithUpload";
import MultiStringInput from "../../../ui-lib/components/MultiInput/MultiStringInput";
import Dropdown from "../../../ui-lib/components/Dropdown/Dropdown";
import Button from "../../../ui-lib/components/Button/Button";
import TemporaryAlarmHandlingModal from "./TemporaryAlarmHandlingModal";
import useSharedObject from "../SharedObjectContext/useSharedObject";

import styles from "./ObjectGeneral.module.css";

const languageOptions = [
  { id: 9, key: "english", flag: "GB" },
  { id: 29, key: "swedish", flag: "SE" },
];

const ObjectGeneralBasic = ({
  defaultValues,
  objectId,
  objectPhoto,
  alarmHandlings,
  setThumbnail,
}: {
  defaultValues: ObjectDetailsModel;
  objectId: string;
  objectPhoto: string;
  alarmHandlings: AlarmHandling[];
  setThumbnail: Dispatch<SetStateAction<string>>;
}) => {
  const t = useTranslations();
  const { config } = useUser();
  const { width } = useWindowDimensions();
  const { isLoading: objectLoading } = useSharedObject();

  const [alarmHandlingsList, setAlarmHandlingsList] = useState<IItemProp[]>([]);
  const [alarmHandlingModalOpened, setAlarmHandlingModalOpened] =
    useState(false);

  const {
    control,
    setValue,
    formState: { errors },
    watch,
  } = useFormContext();

  const organisationId = watch("organizationId");

  const isDesktop = width >= breakpoints.desktop;
  const isCoreDetailsEditable = !!config?.show.find(
    (key) => key === "User/CoreDetails",
  );
  const hasTrygghetsjourenJourledRole = useMemo(
    () => config?.show.includes("ShowObjectDetailsVariant1"),
    [],
  );

  useEffect(() => {
    setAlarmHandlingsList(
      alarmHandlings.map((alarm) => ({
        isSelected: defaultValues.alarmHandlingId === alarm.id,
        id: alarm.id,
        name: alarm.name,
      })),
    );
  }, [defaultValues.alarmHandlingId]);

  return (
    <div className={styles.ObjectBasicContainer}>
      <h2>{t("Objects:general_basic_details_title")}</h2>
      <p>{t("Objects:general_basic_details_description")}</p>

      <Spacer size={16} />
      <TwoColGrid>
        <Column align="start">
          <Controller
            name="name"
            control={control}
            rules={{
              required: true,
              minLength: 2,
              maxLength: 100,
            }}
            render={({ field }) => (
              <TextInput
                required
                inputRef={field.ref}
                dataCy="objectName"
                label={t("Objects:object_name")}
                value={field.value}
                onChange={field.onChange}
                disabled={!isCoreDetailsEditable}
                validationError={errors?.name && t("Errors:input_object_name")}
              />
            )}
          />
          <Spacer size={16} />
          <Controller
            name="lastName"
            control={control}
            rules={{ maxLength: 100 }}
            render={({ field }) => (
              <TextInput
                inputRef={field.ref}
                label={t("Objects:object_lastName")}
                value={field.value}
                onChange={field.onChange}
                disabled={!isCoreDetailsEditable}
                validationError={
                  errors?.lastName &&
                  t("Errors:max_chars_exceeded").replace("{0}", "100")
                }
              />
            )}
          />
          <Spacer size={16} />
          {defaultValues.sellerName && (
            <p data-cy="objectCreationDate">
              <span className="strong">
                {t("Objects:labels_admins_seller")}
              </span>
              {": "}
              {defaultValues.sellerName}
            </p>
          )}
        </Column>
        <Column align="start" className={classNames({ ["flex0"]: !isDesktop })}>
          {!!config?.show.find((key) => key === "User/Photo") && (
            <PhotoWrapperWithUpload
              object={defaultValues}
              buttonName="objectThumbnail"
              propertyToContainData="Userimage"
              preLoadedImageData={objectPhoto}
              photoTitle={t("Common:label_image")}
              alternativeString={`user ${defaultValues.name} with id ${defaultValues.id} thumbnail`}
              setThumbnail={setThumbnail}
            />
          )}
          <Spacer size={16} />
          <p data-cy="objectCreationDate">
            <span className="strong">{t("Common:label_registered_at")}</span>{" "}
            {moment(defaultValues.registrationTime).format("DD MMMM YYYY")}
          </p>
        </Column>
      </TwoColGrid>
      <Spacer size={16} />
      <TwoColGrid>
        <Column align="fill">
          <Controller
            name="organizationId"
            control={control}
            render={({ field }) => (
              <CustomDropdown
                fullWidth
                label={t("Objects:organisation")}
                placeholder={t("Common:select_organisation")}
                customerFilter={{ customers: organisationId.toString() }}
                validationError={
                  errors.organizationId && t("Objects:organisationvalidation")
                }
                onSelectSignle={(data?: ITreeData) => {
                  if (data?.key !== undefined) {
                    setValue(field.name, data.key);
                  }
                }}
                selectionMode="single"
              />
            )}
          />
        </Column>
        <Spacer />
      </TwoColGrid>

      <Spacer size={16} />

      <ResponsiveRow>
        <Controller
          name="responseCenterId"
          control={control}
          render={() => (
            <TextInput
              inputClassName="fw-600"
              dataCy="objectARC"
              label={t("Common:labels_alarm_reception")}
              value={defaultValues.responseCenterName}
              disabled
            />
          )}
        />
        <Spacer />
        <Controller
          name="alarmHandlingId"
          control={control}
          render={() => (
            <Dropdown
              dataCy="objectAlarmHandling"
              onSelectItem={(item) => {
                const updatedHandlingList = alarmHandlingsList.map((alarm) => ({
                  ...alarm,
                  isSelected: alarm.id === item.id,
                }));
                setAlarmHandlingsList(updatedHandlingList);
                setValue("alarmHandlingId", item.id);
                setValue("alarmHandling", item.name);
              }}
              items={alarmHandlingsList}
              title={t("AlarmHistory:alarm_query_filter_alarm_handling")}
              selectedItem={alarmHandlingsList.find(
                (alarm) => alarm.isSelected,
              )}
              disabled={!alarmHandlingsList.length || !isCoreDetailsEditable}
            />
          )}
        />
      </ResponsiveRow>
      <Spacer size={16} />
      <Row type="left" align="end">
        {defaultValues.active && !hasTrygghetsjourenJourledRole && (
          <Button
            className={styles.tmpAlarmHandlingBtn}
            variant="popup"
            text={
              defaultValues.temporaryAlarmHandlingEnd &&
              moment.utc() < moment(defaultValues.temporaryAlarmHandlingEnd)
                ? t("Objects:edit_temporary_alarm_handling")
                : t("Objects:set_temporary_alarm_handling")
            }
            onClick={() => setAlarmHandlingModalOpened(true)}
            disabled={!isCoreDetailsEditable || objectLoading}
          />
        )}
      </Row>

      <Spacer size={16} />

      {config?.show.includes("ShareAlarm") && (
        <Row type="fill">
          <Controller
            name="shareAlarmWith"
            control={control}
            render={({ field }) => {
              const length = field.value?.length ?? 0;
              const values = length > 0 ? field.value?.split(",") : [];

              return (
                <MultiStringInput
                  maxValues={6}
                  label={t("Common:labels_share_alarm_with")}
                  description={t("Common:share_alarm_description")}
                  values={values}
                  setValues={(updated) => {
                    const value = updated.join(",");
                    setValue(field.name, (value || "").toUpperCase(), {
                      shouldDirty: true,
                    });
                  }}
                />
              );
            }}
          />
        </Row>
      )}

      <Spacer size={16} />

      <RoleFeature requires={[Role.BetaAccess]}>
        <TwoColGrid>
          <Controller
            name="culture"
            control={control}
            render={({ field }) => {
              const items = languageOptions.map((l) => ({
                id: l.id,
                name: t(`Common:language_${l.key}`),
                flag: l.flag,
                isSelected: field.value === l.id,
              }));

              const selectedItem = items.find((i) => i.isSelected);

              return (
                <Dropdown
                  items={items}
                  selectedItem={selectedItem}
                  onSelectItem={(item) => setValue("culture", item.id)}
                  title={t("Common:labels_language")}
                  placeholder={t("Common:labels_select_language")}
                  disabled={!isCoreDetailsEditable}
                />
              );
            }}
          />
        </TwoColGrid>
      </RoleFeature>
      <TemporaryAlarmHandlingModal
        isOpen={alarmHandlingModalOpened}
        setIsOpen={setAlarmHandlingModalOpened}
        alarmHandlings={alarmHandlings}
        objectData={defaultValues}
        objectId={objectId}
      />
    </div>
  );
};

export default ObjectGeneralBasic;
